import {
	Button,
	Modal,
	ModalActions,
	ModalContent,
	ModalHeading,
	Paragraph,
} from '@audio-network/componentlibrary'
import { useTranslation } from 'next-i18next'
import { useRef } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import * as StyledForm from '../../../../components/shared/Forms/Forms.styles'
import { httpStatusCode } from '../../../../constants/HttpStatusCodes'
import { sendProject } from '../../../../services/projectsService'
import { CopyToAccountToast } from '../Toasts'

type Props = {
	projectId: string
	id: string
}

const copyToAccountModalHeadingId = 'copy-to-account-modal-heading'
const copyToAccountModalDescriptionId = 'copy-to-account-modal-description'

export default function CopyToAccountModal({
	projectId,
	id,
}: Props): JSX.Element {
	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<{ email: string; allUsers: boolean }>({ mode: 'onSubmit' })

	const modalRef = useRef<HTMLDialogElement>(null)

	const { t } = useTranslation(['projectedit'])

	const copyToAccountFormId = `copy-to-account-form-${id}`

	const handleCopyToAccountSubmit: SubmitHandler<{
		email: string
		allUsers: boolean
	}> = async (data): Promise<void> => {
		// Remove when toggle is removed
		let sendToAllUsers = data.allUsers
		if (sendToAllUsers === undefined) {
			sendToAllUsers = false
		}

		const response: ProjectResponse = await sendProject(
			projectId,
			data.email,
			sendToAllUsers
		)
		const { statusCode } = response || {}

		if (statusCode >= httpStatusCode.InternalServerError || !statusCode) {
			toast.error(
				<CopyToAccountToast
					error
					translations={{
						heading: t('copy-to-account-modal.toast-error-header'),
						message: t('copy-to-account-modal.toast-error-message'),
					}}
				/>
			)
		} else if (
			statusCode >= httpStatusCode.BadRequest &&
			statusCode < httpStatusCode.InternalServerError
		) {
			//currently internal API returns a 400 for user not found
			toast.warning(
				<CopyToAccountToast
					error
					translations={{
						heading: t('copy-to-account-modal.toast-error-header'),
						message: t('copy-to-account-modal.toast-no-account-message'),
					}}
				/>
			)
		} else {
			toast.success(
				<CopyToAccountToast
					email={data.email}
					translations={{
						heading: t('copy-to-account-modal.toast-success-header'),
						message: t('copy-to-account-modal.toast-success-message'),
					}}
				/>
			)
		}

		modalRef.current?.close()
	}

	return (
		<Modal
			aria-describedby={copyToAccountModalDescriptionId}
			aria-labelledby={copyToAccountModalHeadingId}
			id={id}
			ref={modalRef}
		>
			<ModalHeading id={copyToAccountModalHeadingId}>
				{t('copy-to-account-modal.heading')}
			</ModalHeading>

			<ModalContent>
				<Paragraph id={copyToAccountModalDescriptionId}>
					{t('copy-to-account-modal.description')}
				</Paragraph>
				<StyledForm.Form
					id={copyToAccountFormId}
					noValidate
					onSubmit={handleSubmit(handleCopyToAccountSubmit)}
				>
					<StyledForm.FieldLabel
						htmlFor="copyToAccount"
						hasError={errors.email}
					>
						{t('copy-to-account-modal.email')}
					</StyledForm.FieldLabel>
					<StyledForm.FieldWrap hasError={errors.email}>
						<StyledForm.FieldInput
							type="email"
							id="copyToAccount"
							aria-label={t('copy-to-account-modal.email-placeholder')}
							required
							placeholder={t('copy-to-account-modal.email-placeholder')}
							error={errors.email}
							aria-invalid={errors.email ? 'true' : 'false'}
							{...register('email', {
								pattern: {
									/**
									 * We allow between 2 and 63 letters within the top level domain (e.g. .com)
									 * See https://developer.mozilla.org/en-US/docs/Learn/Common_questions/Web_mechanics/What_is_a_domain_name#structure_of_domain_names
									 */
									value: /^[\w-.]+@([\w-]+\.)+[\w-]{2,63}$/,
									message: t('copy-to-account-modal.error'),
								},
								required: t('copy-to-account-modal.error'),
							})}
						/>
					</StyledForm.FieldWrap>
					{errors.email && (
						<StyledForm.ErrorMessage>
							{errors.email.message}
						</StyledForm.ErrorMessage>
					)}
					<StyledForm.FieldCheckboxWrap>
						<StyledForm.FieldCheckbox
							id="all-users"
							type="checkbox"
							style={{ marginLeft: '-0.35rem' }}
							{...register('allUsers')}
						/>
						<StyledForm.FieldCheckboxLabel htmlFor="all-users">
							Make everyone in their organisation a collaborator
						</StyledForm.FieldCheckboxLabel>
					</StyledForm.FieldCheckboxWrap>
				</StyledForm.Form>
			</ModalContent>

			<ModalActions>
				<Button form={copyToAccountFormId} type="submit">
					{t('copy-to-account-modal.send')}
				</Button>
				<Button
					command="close"
					commandFor={id}
					type="button"
					variant="secondary"
				>
					{t('copy-to-account-modal.cancel')}
				</Button>
			</ModalActions>
		</Modal>
	)
}
